import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { environment } from '../../../environments/environment';
import { ApiEndpoints } from '../../core/api-endpoints.service';
import { CookieService } from '../../core/cookie.service';
import { IdService } from '../../core/id.service';
import { JoseService } from '../../core/jose.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';
import { dateInputValue, mergeDateAndTime } from '../../shared/date-utils';

export type UtilityTool =
  | 'revoke'
  | 'capacity'
  | 'change-compartment'
  | 'hardware-ids'
  | 'inspect-event'
  | 'incident-report'
  | 'incident-status'
  | 'instructions';

interface InspectEvent {
  id: string;
  compartmentId: number;
  itemId: string;
  eventTime: string;
  event: string;
}

@Component({
  selector: 'app-utility-tools',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './utility-tools.component.html',
})
export class UtilityToolsComponent {
  readonly initialTool = input<UtilityTool>('revoke');
  readonly showNavigation = input(true);

  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly cookies = inject(CookieService);
  private readonly ids = inject(IdService);
  private readonly jose = inject(JoseService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeTool = signal<UtilityTool>('revoke');
  protected readonly call = new ApiCallState();
  protected readonly inspectEvents = signal<InspectEvent[]>([]);
  protected readonly toolTitle = computed(() => {
    switch (this.activeTool()) {
      case 'revoke':
        return 'Revoke Access';
      case 'capacity':
        return 'Get Capacity';
      case 'change-compartment':
        return 'Change Compartment';
      case 'hardware-ids':
        return 'Get HardwareIds';
      case 'inspect-event':
        return 'Inspect Event';
      case 'incident-report':
        return 'Incident Report';
      case 'incident-status':
        return 'Get Incident Report';
      case 'instructions':
        return 'Get Instructions (AT)';
    }
  });
  protected readonly endpoint = computed(() => {
    switch (this.activeTool()) {
      case 'revoke':
        return this.endpoints.url(this.endpoints.revoke);
      case 'capacity':
        return `${this.endpoints.url(this.endpoints.capacity)}/{country}/{provider}/{lockId}`;
      case 'change-compartment':
        return this.endpoints.url(this.endpoints.changeCompartment);
      case 'hardware-ids':
        return this.endpoints.url(this.endpoints.hardwareIds);
      case 'inspect-event':
        return this.endpoints.url(this.endpoints.inspectEvent);
      case 'incident-report':
        return this.endpoints.url(this.endpoints.incidentReport);
      case 'incident-status':
        return `${this.endpoints.url(this.endpoints.incidentReport)}/{requestId}`;
      case 'instructions':
        return `${environment.instructionUrl}/{itemId}`;
    }
  });

  protected revokeItemId = '';
  protected parcelAction = 'ReturnToSender';

  protected capacityCountry = 'SE';
  protected capacityProvider = '';
  protected capacityLockId = '';
  protected capacityType = 'NONE';
  protected capacityFilter = 'NONE';

  protected changeItemId = '';
  protected newCompartmentSize = '';
  protected changeLockId = '';
  protected changeCountry = 'SE';
  protected changeReason = '';
  protected changeTtlDate = futureDateInput(1);
  protected changeTtlTime = '23:59';
  protected changeKeyUserId = 'PN12345';

  protected hardwareCountry = 'SE';
  protected hardwareUids = '';
  protected hardwareProvider = '';

  protected inspectKeyUserId = 'PN12345';
  protected inspectHardwareId = '';
  protected inspectProvider = '';
  protected inspectCountry = 'SE';
  protected inspectAdditionalInfo = 'test';

  protected incidentRequestId = this.ids.uuid();
  protected incidentKeyUserId = 'PN12345';
  protected incidentReportedBy = 'courier';
  protected incidentHardwareId = '';
  protected incidentDescription = 'test';
  protected incidentProvider = '';
  protected incidentCountry = 'SE';
  protected incidentErrorType = '';
  protected incidentJobType = '3';
  protected incidentCompartmentId: number | undefined;
  protected incidentDeadlineDate = futureDateInput(4);
  protected incidentDeadlineTime = '23:59';

  protected incidentStatusRequestId = this.cookies.read('incidentReportRequestId') ?? '';
  protected instructionItemId = '';

  constructor() {
    effect(() => this.openTool(this.initialTool()));
  }

  protected openTool(tool: UtilityTool): void {
    this.activeTool.set(tool);
    this.call.clear();
    if (tool === 'incident-status') {
      this.incidentStatusRequestId =
        this.cookies.read('incidentReportRequestId') ?? this.incidentStatusRequestId;
    }
  }

  protected submitRevoke(): void {
    const payload = { parcelAction: this.parcelAction, itemId: this.revokeItemId };
    this.post(this.endpoints.url(this.endpoints.revoke), payload, { itemId: this.revokeItemId });
  }

  protected submitCapacity(): void {
    let params = new HttpParams();
    if (this.capacityFilter !== 'NONE') {
      params = params.set('capacityFilter', this.capacityFilter);
    }
    if (this.capacityType !== 'NONE') {
      params = params.set('capacityType', this.capacityType);
    }
    const url = [
      this.endpoints.url(this.endpoints.capacity),
      encodeURIComponent(this.capacityCountry),
      encodeURIComponent(this.capacityProvider),
      encodeURIComponent(this.capacityLockId),
    ].join('/');

    this.call.start({
      country: this.capacityCountry,
      provider: this.capacityProvider,
      lockId: this.capacityLockId,
      capacityFilter: this.capacityFilter,
      capacityType: this.capacityType,
    });
    this.http
      .get(url, { params })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.call.succeed(response),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected submitChangeCompartment(): void {
    const ttl = mergeDateAndTime(this.changeTtlDate, this.changeTtlTime);
    const payload = {
      itemId: this.changeItemId,
      newCompartmentSize: this.newCompartmentSize,
      lockId: this.changeLockId,
      country: this.changeCountry,
      reason: this.changeReason,
      ttl: Math.floor(ttl.getTime() / 1000),
      keyUserId: this.changeKeyUserId,
      encryption: {
        key: this.jose.publicKey,
        alg: this.jose.algorithm,
        enc: this.jose.encryption,
      },
    };
    this.post(this.endpoints.url(this.endpoints.changeCompartment), payload);
  }

  protected submitHardwareIds(): void {
    const params = new HttpParams()
      .set('uid', this.hardwareUids)
      .set('country', this.hardwareCountry)
      .set('provider', this.hardwareProvider);
    this.call.start({
      uid: this.hardwareUids,
      country: this.hardwareCountry,
      provider: this.hardwareProvider,
    });
    this.http
      .get(this.endpoints.url(this.endpoints.hardwareIds), { params })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.call.succeed(response),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected addInspectEvent(): void {
    this.inspectEvents.update((events) => [
      ...events,
      {
        id: this.ids.uuid(),
        compartmentId: 0,
        itemId: '',
        eventTime: new Date().toISOString(),
        event: 'INSPECT_COMPLETE',
      },
    ]);
  }

  protected removeInspectEvent(id: string): void {
    this.inspectEvents.update((events) => events.filter((event) => event.id !== id));
  }

  protected submitInspectEvent(): void {
    const payload = {
      keyUserId: this.inspectKeyUserId || 'PN12345',
      hardwareId: this.inspectHardwareId,
      events: this.inspectEvents().map(({ id: _id, ...event }) => event),
      other: this.inspectAdditionalInfo,
      provider: this.inspectProvider,
      country: this.inspectCountry,
      requestId: this.ids.uuid(),
    };
    this.post(this.endpoints.url(this.endpoints.inspectEvent), payload, {
      message: 'Inspect event reported',
    });
  }

  protected regenerateIncidentRequestId(): void {
    this.incidentRequestId = this.ids.uuid();
  }

  protected submitIncidentReport(): void {
    const deadline = mergeDateAndTime(
      this.incidentDeadlineDate,
      this.incidentDeadlineTime,
    );
    const payload = {
      keyUserId: this.incidentKeyUserId || 'PN12345',
      reportedBy: this.incidentReportedBy,
      hardwareId: this.incidentHardwareId,
      description: this.incidentDescription,
      provider: this.incidentProvider,
      country: this.incidentCountry,
      errorType: this.incidentErrorType,
      jobType: Number(this.incidentJobType),
      requestId: this.incidentRequestId,
      deadline: deadline.toISOString(),
      compartmentId: this.incidentCompartmentId,
    };
    this.call.start(payload);
    this.http
      .post(this.endpoints.url(this.endpoints.incidentReport), payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.cookies.write('incidentReportRequestId', this.incidentRequestId, 60);
          this.incidentStatusRequestId = this.incidentRequestId;
          this.call.succeed({ message: 'Incident reported', response });
        },
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected submitIncidentStatus(): void {
    this.call.start({ requestId: this.incidentStatusRequestId });
    this.http
      .get(
        `${this.endpoints.url(this.endpoints.incidentReport)}/${encodeURIComponent(this.incidentStatusRequestId)}`,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.call.succeed(response),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected submitInstructions(): void {
    this.call.start({ itemId: this.instructionItemId });
    this.http
      .get(`${environment.instructionUrl}/${encodeURIComponent(this.instructionItemId)}`, {
        params: { apikey: environment.instructionApiKey },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.call.succeed(response),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  private post(url: string, payload: unknown, success?: unknown): void {
    this.call.start(payload);
    this.http
      .post(url, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.call.succeed(success ?? response),
        error: (error: unknown) => this.call.fail(error),
      });
  }
}

function futureDateInput(days: number): string {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return dateInputValue(value);
}